import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import NextBestAction from "@/components/NextBestAction";
import usePageSEO from "@/hooks/usePageSEO";

/**
 * Phase 11.5 — plain-English financial glossary, state-licensed language,
 * each term optionally linking to the regulated product it relates to.
 */

const TERMS = [
  { term: "Accelerated Benefits Rider", defn: "A rider that lets a policyholder access part of their life insurance death benefit early if diagnosed with a qualifying critical, chronic, or terminal illness.", link: "/products/term-life" },
  { term: "Annuitization", defn: "The process of converting an annuity's accumulated value into a stream of periodic income payments, typically for life or a set period. Once annuitized, most contracts cannot be reversed.", link: "/products/annuity-income" },
  { term: "Annuity", defn: "A contract issued by an insurance company that provides tax-deferred growth and the option to convert savings into guaranteed income. Guarantees are backed by the issuing carrier.", link: "/products/annuity-accumulation" },
  { term: "Assignment", defn: "The transfer of policy ownership rights (or benefits) to another party, often used for business or estate planning purposes. Requires carrier approval." },
  { term: "Beneficiary", defn: "The person(s) or entity designated to receive the death benefit of a life insurance policy or annuity." },
  { term: "Cap Rate (IUL)", defn: "The maximum interest crediting rate an indexed universal life policy can earn on index-linked gains in a given period. Caps are set by the carrier and can change over time." },
  { term: "Cash Value", defn: "The savings component inside a permanent life insurance policy that grows tax-deferred and can typically be accessed via policy loans while the policy remains in force.", link: "/products/whole-life" },
  { term: "Convertibility", defn: "A feature of certain term life insurance policies that allows conversion to a permanent policy without new medical underwriting, subject to the carrier's rules and conversion deadlines." },
  { term: "Death Benefit", defn: "The amount paid to beneficiaries when the insured person dies. Generally received income-tax-free when paid as a lump sum to a named individual." },
  { term: "Deferred Annuity", defn: "An annuity in which income payments begin at a future date, allowing the account to grow tax-deferred in the meantime.", link: "/products/annuity-accumulation" },
  { term: "Disability Income Insurance (DI)", defn: "Insurance that replaces a portion of your earned income if you become unable to work due to sickness or injury. Definitions of disability vary by policy.", link: "/products/disability-income" },
  { term: "Dividend (Whole Life)", defn: "A return of excess premium paid by a mutual life insurance carrier to policyholders. Dividends are not guaranteed.", link: "/products/whole-life" },
  { term: "Elimination Period", defn: "The waiting period between the start of a disability or LTC claim and when benefits begin to pay. Longer elimination periods generally reduce premiums." },
  { term: "Floor (IUL / FIA)", defn: "The minimum credited interest rate — often 0% — guaranteed on indexed products regardless of index performance. Policy charges still apply, so cash value can decline in a 0% year." },
  { term: "Group vs Individual Coverage", defn: "Group coverage is sponsored by an employer; individual coverage is owned directly by the insured. Individual policies are typically portable across jobs." },
  { term: "Guarantee (Backing)", defn: "In insurance, product guarantees are backed by the financial strength and claims-paying ability of the issuing carrier. They are not FDIC-insured." },
  { term: "HSA (Health Savings Account)", defn: "A triple-tax-advantaged savings account paired with an HSA-qualified high-deductible health plan. Contributions are deductible, growth is tax-free, and qualified withdrawals are tax-free.", link: "/products/hsa-plan" },
  { term: "Illustration (Non-Guaranteed)", defn: "A hypothetical projection of policy performance based on current assumed interest crediting, caps, or dividends. Non-guaranteed elements can change." },
  { term: "Index Universal Life (IUL)", defn: "Permanent life insurance whose cash value growth is tied to the movement of a market index, with a floor (often 0%) and a cap. Not a direct investment in the index.", link: "/products/iul-accumulation" },
  { term: "Irrevocable Life Insurance Trust (ILIT)", defn: "A trust that owns a life insurance policy outside of the insured's estate, used to remove the death benefit from estate-tax exposure. Requires coordination with an estate attorney.", link: "/products/estate-ilit" },
  { term: "Long-Term Care Insurance (LTC)", defn: "Insurance that pays benefits when the insured can no longer perform a specified number of activities of daily living (ADLs) or suffers cognitive impairment.", link: "/products/ltc" },
  { term: "MEC (Modified Endowment Contract)", defn: "A life insurance policy funded faster than IRS 7-pay limits. Loans and withdrawals from MECs are taxed less favorably than non-MEC policies." },
  { term: "Medicare Advantage (Part C)", defn: "A private-plan alternative to Original Medicare that bundles hospital, medical, and often prescription drug coverage into one plan. Networks and benefits vary.", link: "/products/medicare" },
  { term: "Medigap (Medicare Supplement)", defn: "Private insurance designed to pay for costs Original Medicare does not fully cover, such as deductibles and coinsurance.", link: "/products/medicare" },
  { term: "Own-Occupation Disability", defn: "A DI definition that pays full benefits if you cannot perform the material duties of your specific occupation, even if you could work in a different field.", link: "/products/disability-income" },
  { term: "Participation Rate", defn: "The percentage of an index's gain credited to an indexed annuity or IUL in a given period. Participation rates can be adjusted by the carrier." },
  { term: "Policy Loan", defn: "A loan taken against the cash value of a permanent life insurance policy. Loans reduce the death benefit if unpaid and can cause policy lapse if poorly managed." },
  { term: "Premium", defn: "The amount you pay — monthly, annually, or as a single payment — to keep an insurance contract or annuity in force." },
  { term: "Rider", defn: "An optional policy feature that adds or modifies benefits, often at additional cost. Examples: accelerated benefits, chronic illness, long-term care, waiver of premium." },
  { term: "Section 7702", defn: "The portion of the U.S. Internal Revenue Code that defines a life insurance contract for tax purposes, including the limits that preserve favorable tax treatment of cash value and death benefit." },
  { term: "Surrender Charge", defn: "A fee imposed when a policyholder withdraws more than the allowed free amount from an annuity or cash-value life insurance policy during the surrender period." },
  { term: "Suitability (NAIC)", defn: "A regulated standard requiring that insurance and annuity recommendations be appropriate for the client's financial situation, needs, and objectives at the time of sale." },
  { term: "Term Life Insurance", defn: "Life insurance that provides level coverage for a defined period (e.g., 10, 20, 30 years). No cash value unless specific riders are added.", link: "/products/term-life" },
  { term: "TPMO (Medicare)", defn: "Third-Party Marketing Organization. CMS requires specific disclosures when a licensed agent represents multiple Medicare Advantage or Part D plans.", link: "/compliance#medicare-tpmo" },
  { term: "Underwriting", defn: "The process by which a carrier evaluates the risk presented by an applicant to determine coverage eligibility, pricing (health class), and policy terms." },
  { term: "Whole Life Insurance", defn: "Permanent life insurance with level premiums, a guaranteed death benefit, and a guaranteed-minimum cash value that grows throughout the policyholder's life.", link: "/products/whole-life" },
  { term: "529 Plan", defn: "A tax-advantaged education savings account. Qualified withdrawals for education expenses are federal tax-free; non-qualified withdrawals face income tax plus a 10% penalty on earnings.", link: "/products/529-college" },
];

export default function Glossary() {
  const [q, setQ] = useState("");

  usePageSEO({
    title: "Glossary of Insurance, Annuity and Wealth Terms",
    description:
      "Plain-English, NAIC-aligned definitions for annuities, IUL, long-term care, 529 plans, estate planning and more — 37 terms, searchable, cross-linked to relevant products.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      name: "Lifespan Wealth Partners Glossary",
      description:
        "Plain-English financial definitions for insurance, annuity and estate products.",
      hasDefinedTerm: TERMS.map((t) => ({
        "@type": "DefinedTerm",
        name: t.term,
        description: t.defn,
      })),
    },
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return TERMS;
    return TERMS.filter(
      (t) =>
        t.term.toLowerCase().includes(needle) ||
        t.defn.toLowerCase().includes(needle),
    );
  }, [q]);

  const grouped = useMemo(() => {
    const byLetter = {};
    filtered.forEach((t) => {
      const l = t.term[0].toUpperCase();
      byLetter[l] = byLetter[l] || [];
      byLetter[l].push(t);
    });
    return Object.entries(byLetter).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div data-testid="glossary-page" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="lw-eyebrow">Glossary</span>
          <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl text-lw-navy leading-[1.05]">
            Plain-English definitions{" "}
            <span className="italic">for regulated products.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-lw-muted">
            A working vocabulary for the life insurance, annuity, long-term
            care, and health instruments we work with. Educational only — not
            legal, tax, or investment advice.
          </p>

          <div className="mt-8 max-w-xl relative">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-lw-muted"
            />
            <input
              data-testid="glossary-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search terms (e.g. annuity, IUL, 529)"
              className="w-full rounded-full border border-lw-navy/15 bg-white pl-11 pr-4 py-3 text-sm placeholder:text-lw-muted/70 focus:outline-none focus:ring-2 focus:ring-lw-gold/40 focus:border-lw-gold"
            />
          </div>

          <div className="mt-10 gold-hairline mb-10" />

          <div data-testid="glossary-list" className="space-y-10">
            {grouped.length === 0 && (
              <p className="text-sm text-lw-muted">
                No matches. Try a different word.
              </p>
            )}
            {grouped.map(([letter, items]) => (
              <section key={letter} data-testid={`glossary-letter-${letter}`}>
                <h2 className="font-serif-display text-2xl text-lw-gold">
                  {letter}
                </h2>
                <dl className="mt-4 space-y-5">
                  {items.map((t) => (
                    <div
                      key={t.term}
                      data-testid={`glossary-term-${t.term.replace(/\s+/g, "-").toLowerCase()}`}
                      className="grid sm:grid-cols-12 gap-3 sm:gap-6 items-baseline"
                    >
                      <dt className="sm:col-span-4 font-serif-display text-lg text-lw-navy leading-snug">
                        {t.term}
                      </dt>
                      <dd className="sm:col-span-8 text-sm leading-relaxed text-lw-ink">
                        {t.defn}
                        {t.link && (
                          <>
                            {" "}
                            <Link
                              to={t.link}
                              className="text-lw-gold hover:underline font-medium"
                              data-testid={`glossary-link-${t.term.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                              Related →
                            </Link>
                          </>
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            ))}
          </div>

          <div className="mt-16 rounded-xl border border-lw-navy/10 bg-white p-6 text-xs text-lw-muted leading-relaxed">
            Definitions on this page are educational summaries, not legal
            definitions. Actual contract language varies by carrier, state,
            and policy. Please consult the policy itself and a licensed
            professional for authoritative detail.
          </div>
        </div>

        <NextBestAction
          pageId="glossary"
          step={2}
          heading="See the products that use these concepts"
          rationale="You've got the vocabulary. The next step is seeing how these concepts shape actual products — browse our solutions and we'll link each back to a definition."
          primary={{ label: "See our products", to: "/#products", testid: "nba-glossary-primary" }}
          secondary={{ label: "Get the plain-English guide", to: "/learn", testid: "nba-glossary-secondary" }}
        />
      </main>
      <Footer />
    </div>
  );
}
